import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { startWith, takeUntil } from 'rxjs/operators';
import { IntellioFormlyWrapperComponent } from './itc-formly-wrapper.component';
import { WrapperChangeDetectNotifierService } from './wrapper-change-detect-notifier.service';
import {
  evaluateFormlyConditions,
  shouldEnableFieldFromParentCascade,
  shouldEnableFormlyFieldControls,
} from './conditional-hide.utils';

@Component({
  selector: 'itc-conditional-hide',
  template: `
    <div [hidden]="!shouldRender">
      <ng-template #fieldComponent></ng-template>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConditionalHideComponent
  extends IntellioFormlyWrapperComponent
  implements OnInit, OnDestroy
{
  shouldRender = null;
  shouldEnableControls = null;
  rootForm: AbstractControl;
  root: FormlyFieldConfig;

  constructor(
    protected notifierService: WrapperChangeDetectNotifierService,
    protected cd: ChangeDetectorRef
  ) {
    super(notifierService, cd);
  }

  ngOnInit(): void {
    let root = this.field;
    this.rootForm = root.form;
    let isRoot = false;
    while (!isRoot) {
      if (!root.parent) {
        isRoot = true;
      } else {
        root = root.parent;
      }
    }
    this.root = root;

    // Option 1. Fire all conditionals whenever a
    // change occurs anywhere on the form
    // root.formControl.valueChanges
    //   .pipe(startWith(undefined), takeUntil(this.destroyed$))
    //   .subscribe(() => {
    //     this.render(this.field, root);
    //     console.log(this.field.key);
    //   });

    // Option 2. Subscribe only to value changes
    // on the fields that would cause this conditional
    // to change values
    const conditions = this.field.props.condition;
    // Dedupe subscriptions: multiple predicates that point at the same
    // control would otherwise each trigger their own render() per keystroke.
    const subscribedCtrls = new Set<AbstractControl>();
    let evaluatedOnce = false;
    conditions.forEach((cond) => {
      if (!cond.ignoreChanges) {
        const f = cond.keyIsGlobal
          ? root.formControl
          : this.field.parent.formControl;
        const ctrl = f.get(cond.key);
        if (ctrl !== null && ctrl !== undefined) {
          if (subscribedCtrls.has(ctrl)) {
            return;
          }
          subscribedCtrls.add(ctrl);
          ctrl.valueChanges
            .pipe(startWith(undefined), takeUntil(this.destroyed$))
            .subscribe(() => {
              this.render(this.field, root);
            });
        } else {
          console.log(
            `${cond.key} is not associated with a form control - consider setting "keyIsGlobal: true" on ${this.field.key} if this is an expected form control`
          );
        }
      } else if (!evaluatedOnce) {
        // ignoreChanges conditions only need a single render — they don't
        // listen for value updates. Multiple ignoreChanges predicates would
        // trigger redundant render() calls; collapse to one.
        evaluatedOnce = true;
        this.render(this.field, root);
      }
    });

    if (subscribedCtrls.size === 0 && !evaluatedOnce) {
      this.render(this.field, root);
    }

    this.initChangeDetectSub();
  }

  ngOnDestroy() {
    this.destroyed$.next(null);
    this.destroyed$.complete();
  }

  recursivelyClearFields(field: FormlyFieldConfig) {
    const group = field.fieldGroup;
    for (let i = 0; i < group.length; i++) {
      if (group[i].fieldGroup) {
        this.recursivelyClearFields(group[i]);
      } else if (group[i].key) {
        // A hidden conditional branch must not contribute its configured
        // defaults to the form model. Defaults are restored when the branch
        // becomes visible again.
        group[i].formControl.reset(null);
        if (group[i].formControl.enabled) {
          group[i].formControl.disable({
            onlySelf: true,
            emitEvent: false,
          });
        }
      }
    }
  }

  recursivelyEnableFields(field: FormlyFieldConfig) {
    if (!shouldEnableFormlyFieldControls(field, this.root)) {
      this.recursivelyDisableFields(field);
      return;
    }

    const group = field.fieldGroup;
    if (field.key) {
      if (field.formControl.disabled) {
        field.formControl.enable({
          onlySelf: true,
          emitEvent: false,
        });
        // as we recurse through the controls
        // make sure we only call reset on FormControl
        // instances and not FormGroup instances
        // so as not to get an exception thrown
        if (field.formControl instanceof FormControl) {
          field.formControl.reset(field.defaultValue ?? null);
        }
      }
    }
    for (let i = 0; i < group.length; i++) {
      if (group[i].fieldGroup) {
        this.recursivelyEnableFields(group[i]);
      } else if (group[i].key) {
        const shouldEnableChild = shouldEnableFieldFromParentCascade(
          group[i],
          this.root
        );

        if (shouldEnableChild) {
          if (group[i].formControl.disabled) {
            group[i].formControl.enable({
              onlySelf: true,
              emitEvent: false,
            });
          }
          if (
            group[i].formControl.value === null ||
            group[i].formControl.value === undefined
          ) {
            if (
              group[i].defaultValue !== null &&
              group[i].defaultValue !== undefined
            )
              group[i].formControl.reset(group[i].defaultValue);
          }
        } else if (!shouldEnableChild && group[i].formControl.enabled) {
          group[i].formControl.disable({
            onlySelf: false,
            emitEvent: false,
          });
        }
      }
    }
  }

  recursivelyDisableFields(field: FormlyFieldConfig) {
    if (field.key && field.formControl?.enabled) {
      field.formControl.disable({
        onlySelf: false,
        emitEvent: false,
      });
    }

    const group = field.fieldGroup ?? [];
    for (let i = 0; i < group.length; i++) {
      if (group[i].fieldGroup) {
        this.recursivelyDisableFields(group[i]);
      } else if (group[i].key && group[i].formControl.enabled) {
        group[i].formControl.disable({
          onlySelf: false,
          emitEvent: false,
        });
      }
    }
  }

  // This component determines if we should
  // show or hide a form section or field
  // based on a set of conditions.
  // If it is determined that we are hiding a
  // field/section that was previously visible
  // we also clear the associated form and model
  // data. See the steps below for the implementation.
  render(field: FormlyFieldConfig, root: FormlyFieldConfig) {
    // TODO: use the shared function called shouldRender in
    // the form-data.utility.ts file
    // 1. Compute the next value that
    // will be saved to this.shouldRender.
    // Note: we used to allocate an arrow-function predicate per render() to
    // close over field/root. That allocates a new closure on every keystroke
    // routed through this wrapper. Inlining the call keeps the iteration
    // call-site monomorphic and avoids the per-call allocation.
    const nextShouldRenderVal = evaluateFormlyConditions(field, root);
    const nextShouldEnableControls = shouldEnableFormlyFieldControls(
      field,
      root
    );

    // 2. Save off the current value of this.shouldRender
    // for comparison later
    const shouldRenderTemp = this.shouldRender;
    const shouldEnableControlsTemp = this.shouldEnableControls;

    // 3. Assign the next value of shouldRender
    // to this.shouldRender. Note: we are doing this here
    // to prevent infinite loops through the
    // .valueChanges observable
    // when we call formControl.reset() later
    // in the code. Note: this assignment needs to
    // happen before invoking formControl.reset()!
    this.shouldRender = nextShouldRenderVal;
    this.shouldEnableControls = nextShouldEnableControls;
    const shouldEnforceDisabledState =
      !nextShouldEnableControls &&
      (shouldEnableControlsTemp === null ||
        nextShouldEnableControls !== shouldEnableControlsTemp ||
        field.formControl?.enabled);
    const shouldEnforceEnabledState =
      nextShouldEnableControls &&
      (nextShouldEnableControls !== shouldEnableControlsTemp ||
        field.formControl?.disabled);

    // 4. Check if the new value of this.shouldRender is set to false
    // and the prior value of this.shouldRender was true.
    // i.e. a field that was previously visible is now going to be hidden
    // if (!nextShouldRenderVal && nextShouldRenderVal !== shouldRenderTemp) {
    //   // 5. reset the formControl and broadcast to all form elements that
    //   // they should recompute their values and properties
    //   // Note: due to the checks we put in place in steps 1 - 4
    //   // we are able to prevent infinite loops through
    //   // the .valueChanges observable this method is subscribing to
    //   if (field.wrappers?.includes('form-field')) {
    //     field.formControl.reset(null);
    //   }

    //   // 6. delete all nested associated fields in its model
    //   // since they are no longer visible
    //   if (field.fieldGroup) {
    //     this.recursivelyClearFields(field);
    //   }
    // }

    if (shouldEnforceDisabledState) {
      if (field.wrappers?.includes('form-field')) {
        field.formControl.reset(null);
        field.formControl.disable({
          onlySelf: true,
          emitEvent: false,
        });
      } else if (
        field.key &&
        (field.type === 'file' || field.type === 'typeahead')
      ) {
        // file currently are the only form inputs
        // that do not have form-fields wrappers applied to
        // them. Thus, we need this check
        field.formControl.reset(null);
        field.formControl.disable({
          onlySelf: true,
          emitEvent: false,
        });
      }

      /*
      If the field is a field group and has a key, it is typically
      indicative of a form partial inserted for a repeated section.
      so it is a container for a section but also has its own unique key
      */
      if (field.fieldGroup && field.key) {
        field.formControl.reset(null);
        //disables all nested fields without emitting a value change or status change event
        field.formControl.disable({
          emitEvent: false,
          onlySelf: false,
        });

        field.formControl.disable({
          emitEvent: true,
          onlySelf: true,
        });
        //This disables the top level fields (the field representing where the partial goes)
        //and this must emit an event so other fields based on the status of the partial can
        //re-evaluate their logic
      } else if (field.fieldGroup) {
        this.recursivelyClearFields(field);
        if (
          field.formControl &&
          Object.keys((field.formControl as UntypedFormGroup).controls).every(
            (key) =>
              (field.formControl as UntypedFormGroup).controls[key].disabled
          )
        ) {
          field.formControl.disable({
            onlySelf: true,
            emitEvent: false,
          });
        }
      }
    }

    if (shouldEnforceEnabledState) {
      if (field.wrappers?.includes('form-field')) {
        field.formControl.enable({
          onlySelf: true,
          emitEvent: false,
        });
        // was hidden; now we are showing it.
        // -> Set the value to the default or null
        if (shouldRenderTemp === false) {
          if (field.defaultValue !== null && field.defaultValue !== undefined) {
            field.formControl.reset(field.defaultValue);
          } else {
            field.formControl.reset(null);
          }
        }
      } else if (
        field.key &&
        (field.type === 'file' || field.type === 'typeahead')
      ) {
        field.formControl.enable({
          onlySelf: true,

          emitEvent: false,
        });

        // was hidden; now we are showing it.

        // -> Set the value to the default or null

        if (shouldRenderTemp === false) {
          if (field.defaultValue !== null && field.defaultValue !== undefined) {
            field.formControl.reset(field.defaultValue);
          } else {
            field.formControl.reset(null);
          }
        }
      }

      if (field.fieldGroup && field.key) {
        //re-enable the top level partial, and all of the nested fields
        //this ensures any observables to the nested fields update properly
        field.formControl.enable({
          emitEvent: true,
        });
        this.recursivelyEnableFields(field);
      } else if (field.fieldGroup) {
        this.recursivelyEnableFields(field);
      }

      if (field.fieldGroup && field.key) {
        //similarly re-enable the top level partial here
        //but dont emit an event because it should have been emitted previously
        field.formControl.enable({
          emitEvent: false,
          onlySelf: true,
        });
      }
    }

    if (
      nextShouldRenderVal !== shouldRenderTemp ||
      nextShouldEnableControls !== shouldEnableControlsTemp
    ) {
      this.cd.markForCheck();
    }
  }
}
